import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import firebaseService from "../../../firebase_service/firebaseService";
import { MonitorType } from "../../../api/admin";
import { useKlydoMonitors } from "../../KlydoMonitor/hooks/useKlydoMonitors";
import {
  DeviceCommand,
  isVideo,
  MODAL_ACTIONS_INFO,
  resizeCloudinary,
} from "../../../utils/util";
import AdbShellStreamModal from "../.././AdbShellStreamModal";
import LogcatStreamModal from "../.././LogcatStreamModal";
import RoomDbViewerModal from "../.././RoomDbViewerModal";
import SqlQueryStreamModal from "../.././SqlQueryStreamModal";
import KlydosPickDialog from "./../../Gifts/GiftCategories/components/KlydosPickDialog";
import DSDialog from "./../../system-design/DSDialog";
import DSDialogTitle from "./../../system-design/DSDialogTitle";
import DSDialogContent from "./../../system-design/DSDialogContent";
import DSDialogActions from "./../../system-design/DSDialogActions";
import DSBox from "../../system-design/DSBox";
import DSAlert from "../../system-design/DSAlert";
import DSCheckBox from "../../system-design/DSCheckBox";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";
import DSTextField from "../../system-design/DSTextField";
import DSIconButton from "../../system-design/DSIconButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { usePlayKlydos } from "../../Klydos/hooks/useSendKlydoModal";
import useUploadTemporarySoundMutation from "../hooks/useUploadTemporarySoundMutation";
import AudioPlayer from "../../../components/AudioPlayer/AudioPlayer";
import DSTypography from "../../system-design/DSTypography";

/**
 * Window, in seconds, to spread a broadcast `refreshConfig` over: roughly ten clocks
 * per second, clamped to an hour (the clock clamps there too). Deliberately coarse —
 * the point is only to avoid a single-second spike.
 */
const refreshConfigSpreadSeconds = (deviceCount: number): number =>
  Math.min(3600, Math.max(1, Math.ceil(deviceCount / 10)));

/**
 * Optional `expiresAt` on a command. Without it, the klydo-api
 * delete-old-commands cron deletes the command after ~1 day. With it, the
 * command stays until that time, so a clock that is offline runs it when it
 * reconnects. It can only extend the default, and we cap it at a month.
 */
const MAX_COMMAND_EXPIRY_DAYS = 30;

/**
 * Commands that must not run late on a clock that reconnects days later.
 * They always use the default expiry. runAging has no backoffice action yet;
 * it is listed so it stays blocked if one is added.
 */
const COMMANDS_WITHOUT_EXPIRY = new Set(["playKlydo", "playSound", "runAging"]);

const maxCommandExpiresAt = (): Date =>
  new Date(Date.now() + MAX_COMMAND_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

/** Formats a Date as the local-time value of a datetime-local input. */
const toDateTimeLocalValue = (date: Date): string => {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const getExpiresAtError = (value: string): string => {
  if (!value) return "";
  const expiresAt = new Date(value).getTime();
  if (Number.isNaN(expiresAt)) return "Invalid date";
  if (expiresAt <= Date.now()) return "Expiry must be in the future";
  if (expiresAt > maxCommandExpiresAt().getTime())
    return `Expiry can't be more than ${MAX_COMMAND_EXPIRY_DAYS} days away`;
  return "";
};

function ScheduleBulkTaskModal({
  open,
  handleClose,
  action,
  selectedDevices,
  deviceVersion,
  deviceLastVersion,
  hardVersionValue,
}: {
  open: boolean;
  handleClose: (b: boolean) => void;
  action: DeviceCommand;
  selectedDevices: Array<string>;
  deviceVersion?: number;
  deviceLastVersion?: string | number;
  hardVersionValue?: string | number;
}) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    dateError: "",
    klydoError: "",
    serverError: "",
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [params, setParams] = useState<Array<any>>([]);
  const [includeDebugLogs, setIncludeDebugLogs] = useState<boolean>(false);
  const [soundUrl, setSoundUrl] = useState<string>("");
  const [soundVolume, setSoundVolume] = useState<string>("");
  const [soundUploadError, setSoundUploadError] = useState<string>("");
  const [normalizeSound, setNormalizeSound] = useState<boolean>(false);
  const { mutateAsync: uploadSound, isPending: isUploadingSound } =
    useUploadTemporarySoundMutation();
  const [rootMigration, setRootMigration] = useState<boolean>(false);
  const [roomMigration, setRoomMigration] = useState<boolean>(false);
  const [rebuildRoomDb, setRebuildRoomDb] = useState<boolean>(false);
  const [showCmdSummary, setShowCmdSummary] = useState(false);
  const [isKlydoModalOpen, setIsKlydoModalOpen] = useState(false);
  // Clock-internal monitor names for RUN_MONITORS. Empty runs every monitor
  // on the device.
  const [monitorNames, setMonitorNames] = useState<string[]>([]);
  // datetime-local value. Empty keeps the default expiry (~1 day).
  const [expiresAt, setExpiresAt] = useState<string>("");
  const expiresAtError = getExpiresAtError(expiresAt);
  const allowsExpiry = !COMMANDS_WITHOUT_EXPIRY.has(
    MODAL_ACTIONS_INFO[action].command,
  );

  const { data: monitors } = useKlydoMonitors();
  const clockMonitorNameOptions = (monitors ?? [])
    .filter((monitor) => monitor.type === MonitorType.ClockInternal)
    .map((monitor) => monitor.name)
    .sort();

  const handlePickKlydo = (selectedKlydoIds: string[]) => {
    if (selectedKlydoIds.length === 0) return;
    setParams(selectedKlydoIds);
  };

  useEffect(() => {
    if (!open) {
      setParams([]);
      setErrors({ dateError: "", klydoError: "", serverError: "" });
      setIncludeDebugLogs(false);
      setSoundUrl("");
      setSoundVolume("");
      setSoundUploadError("");
      setNormalizeSound(false);
      setRootMigration(false);
      setRoomMigration(false);
      setRebuildRoomDb(false);
      setMonitorNames([]);
      setExpiresAt("");
      setLoading(false);
    }
  }, [open]);
  const [isLogcatOpen, setIsLogcatOpen] = useState(false);
  const [isRoomDbOpen, setIsRoomDbOpen] = useState(false);
  const [isSqlStreamOpen, setIsSqlStreamOpen] = useState(false);
  const [isAdbShellOpen, setIsAdbShellOpen] = useState(false);

  const { playKlydos } = usePlayKlydos({
    deviceIds: selectedDevices,
  });

  function getHeadline(action: DeviceCommand) {
    return MODAL_ACTIONS_INFO[action].headline;
  }

  function handleLogcat() {
    setLoading(true);
    const localParams = ["wss://clock-socket-917257785826.us-central1.run.app"];
    const id = firebaseService.genFbID();
    const deviceId = selectedDevices[0];
    firebaseService
      .sendCommandToClocksByBatch({
        id: id,
        name: MODAL_ACTIONS_INFO[action].command,
        local: false,
        devices: [deviceId],
        date: new Date(),
        params: localParams,
      })
      .catch((error) => {
        console.log("Failed to schedule command: " + error);
        setErrors({
          ...errors,
          serverError: "Failed to schedule command: " + error,
        });
        handleClose(true);
      })
      .finally(() => {
        setLoading(false);
      });

    setIsLogcatOpen(true);
  }

  function handleRoomDb() {
    setLoading(true);
    const localParams = ["wss://clock-socket-917257785826.us-central1.run.app"];
    const id = firebaseService.genFbID();
    const deviceId = selectedDevices[0];
    firebaseService
      .sendCommandToClocksByBatch({
        id: id,
        name: MODAL_ACTIONS_INFO[action].command,
        local: false,
        devices: [deviceId],
        date: new Date(),
        params: localParams,
      })
      .catch((error) => {
        console.log("Failed to schedule command: " + error);
        setErrors({
          ...errors,
          serverError: "Failed to schedule command: " + error,
        });
        handleClose(true);
      })
      .finally(() => {
        setLoading(false);
      });

    setIsRoomDbOpen(true);
  }

  function handleSqlStream() {
    setLoading(true);
    const localParams = ["wss://clock-socket-917257785826.us-central1.run.app"];
    const id = firebaseService.genFbID();
    const deviceId = selectedDevices[0];
    firebaseService
      .sendCommandToClocksByBatch({
        id: id,
        name: MODAL_ACTIONS_INFO[action].command,
        local: false,
        devices: [deviceId],
        date: new Date(),
        params: localParams,
      })
      .catch((error) => {
        console.log("Failed to schedule command: " + error);
        setErrors({
          ...errors,
          serverError: "Failed to schedule command: " + error,
        });
        handleClose(true);
      })
      .finally(() => {
        setLoading(false);
      });

    setIsSqlStreamOpen(true);
  }

  function handleAdbShell() {
    setLoading(true);
    const localParams = ["wss://clock-socket-917257785826.us-central1.run.app"];
    const id = firebaseService.genFbID();
    const deviceId = selectedDevices[0];
    firebaseService
      .sendCommandToClocksByBatch({
        id: id,
        name: MODAL_ACTIONS_INFO[action].command,
        local: false,
        devices: [deviceId],
        date: new Date(),
        params: localParams,
      })
      .catch((error) => {
        console.log("Failed to schedule command: " + error);
        setErrors({
          ...errors,
          serverError: "Failed to schedule command: " + error,
        });
        handleClose(true);
      })
      .finally(() => {
        setLoading(false);
      });

    setIsAdbShellOpen(true);
  }

  async function handleSubmit() {
    setLoading(true);
    let localParams = params;
    if (action === DeviceCommand.PREVIEW || action === DeviceCommand.DELETE)
      localParams = params.map((klydo) => klydo.id);
    if (action === DeviceCommand.SYNC_LOGS) localParams = [includeDebugLogs];
    if (action === DeviceCommand.RUN_MIGRATIONS) {
      localParams = [];
      if (rootMigration) localParams.push("ROOT_MIGRATION");
      if (roomMigration) localParams.push("ROOM_MIGRATION");
      if (rebuildRoomDb) localParams.push("REBUILD_ROOM_DB");
    }

    try {
      if (action === DeviceCommand.PREVIEW) {
        const success = await playKlydos(params);
        if (success) {
          handleClose(false);
          setShowCmdSummary(false);
        } else {
          setErrors({
            ...errors,
            serverError: "Failed to play klydos",
          });
        }
        setLoading(false);
        return;
      }

      if (action !== DeviceCommand.RUN_MIGRATIONS) {
        localParams = params;
      }
      if (action === DeviceCommand.SYNC_LOGS) localParams = [includeDebugLogs];
      if (action === DeviceCommand.PLAY_SOUND)
        localParams = soundVolume.trim()
          ? [soundUrl.trim(), soundVolume.trim()]
          : [soundUrl.trim()];
      // Array of monitor names to run; empty array runs all monitors.
      if (action === DeviceCommand.RUN_MONITORS) localParams = monitorNames;

      // Spread a broadcast config refresh over a window instead of putting every
      // clock on clock/config in the same second — the daily fetch already jitters
      // its minute for exactly this reason. One clock refreshes immediately, so
      // testing a single device stays instant. This side picks the window because
      // it is the side that knows the fan-out; the clock just honours it.
      if (action === DeviceCommand.REFRESH_CONFIG) {
        localParams =
          selectedDevices.length > 1
            ? [String(refreshConfigSpreadSeconds(selectedDevices.length))]
            : [];
      }

      const id = firebaseService.genFbID();

      await firebaseService.sendCommandToClocksByBatch({
        id: id,
        name: MODAL_ACTIONS_INFO[action].command,
        local: false,
        devices: selectedDevices,
        date: new Date(),
        params: localParams,
        expiresAt: allowsExpiry && expiresAt ? new Date(expiresAt) : undefined,
      });

      handleClose(false);
      setShowCmdSummary(false);
    } catch (error) {
      console.log("Failed to schedule command: " + error);
      setErrors({
        ...errors,
        serverError: "Failed to schedule command: " + error,
      });
      handleClose(true);
    } finally {
      setLoading(false);
    }
  }

  const actionButtonSx = {
    fontSize: "85%",
    height: "fit-content",
    width: "fit-content",
  };

  const ActionButton = () => {
    return (
      <DSBox sx={{ width: "fit-content" }}>
        <DSPrimaryButton
          disabled={
            action ===
              (DeviceCommand.DELETE || action === DeviceCommand.PREVIEW) &&
            params.length === 0
          }
          onClick={() => setShowCmdSummary(true)}
          size="small"
          sx={{ ...actionButtonSx, width: "100%" }}
        >
          {getHeadline(action)}
        </DSPrimaryButton>
      </DSBox>
    );
  };

  return (
    <>
      {!showCmdSummary && !isLogcatOpen && !isAdbShellOpen && (
        <DSDialog fullWidth open={open} onClose={() => handleClose(false)}>
          <DSDialogTitle>
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <DSTypography variant="h6">{getHeadline(action)}</DSTypography>
              <DSIconButton
                aria-label="close"
                onClick={() => {
                  handleClose(false);
                }}
              >
                <CloseIcon />
              </DSIconButton>
            </DSBox>
            {action === DeviceCommand.UPDATE && (
              <DSTypography sx={{ color: "text.secondary" }}>
                {deviceLastVersion === "upToDate" ? (
                  <>You are up to date!</>
                ) : (
                  <>
                    You are currently on version:{" "}
                    <strong>{deviceVersion}</strong>.
                    <br />
                    Version{" "}
                    <strong>{hardVersionValue || deviceLastVersion}</strong> is
                    available, do you wish to install?
                  </>
                )}
              </DSTypography>
            )}
          </DSDialogTitle>
          <DSDialogContent
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {action === DeviceCommand.DELETE && params.length > 0 && (
              <>
                <DSBox
                  sx={{
                    width: "90%",
                    display: "flex",
                    justifyContent: "space-around",
                    fontSize: "12px",
                    color: "text.secondary",
                    px: 2,
                    py: 1,
                  }}
                >
                  <DSBox sx={{ width: "105px" }}>IDF</DSBox>
                  <DSBox sx={{ width: "105px" }}>Author</DSBox>
                  <DSBox sx={{ width: "35px" }}>GIF</DSBox>
                  <DSBox sx={{ width: "12px" }}>Pool</DSBox>
                  <DSBox sx={{ width: "6px" }}></DSBox>
                </DSBox>
                <DSBox
                  sx={{
                    width: "90%",
                    maxHeight: "190px",
                    overflowY: "auto",
                  }}
                >
                  {params.map((klydo) => (
                    <DSBox key={klydo.id} sx={{ px: 2 }}>
                      <DSBox
                        sx={{
                          fontSize: "13px",
                          display: "flex",
                          justifyContent: "space-around",
                          alignItems: "center",
                        }}
                      >
                        <DSBox sx={{ width: "105px" }} title={klydo.id}>
                          {klydo.idf}
                        </DSBox>
                        <DSBox sx={{ width: "105px" }} title={klydo.creator}>
                          {klydo.author}
                        </DSBox>
                        {!klydo.loopUrl || isVideo(klydo.loopUrl) === "none" ? (
                          <DSBox
                            sx={{
                              width: "35px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <p>--</p>
                          </DSBox>
                        ) : (
                          <DSBox
                            sx={{
                              borderRadius: "50%",
                              width: "35px",
                              height: "35px",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            <DSBox
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                position: "absolute",
                                top: "0%",
                                left: "0%",
                                width: "100%",
                                aspectRatio: "1/1",
                              }}
                            >
                              {isVideo(klydo.loopUrl) === "image" ? (
                                <img
                                  loading="lazy"
                                  alt="gif"
                                  src={resizeCloudinary(klydo.loopUrl, 48)}
                                  style={{
                                    width: "auto",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <video
                                  muted={true}
                                  src={resizeCloudinary(klydo.loopUrl, 48)}
                                  loop
                                  autoPlay
                                  style={{
                                    width: "auto",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              )}
                            </DSBox>
                          </DSBox>
                        )}
                        <DSBox sx={{ width: "12px" }}></DSBox>
                        <DSIconButton
                          size="small"
                          aria-label="remove"
                          onClick={() => {
                            const tmp = params.filter(
                              (k) => !(k.id === klydo.id),
                            );
                            setParams([...tmp]);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </DSIconButton>
                      </DSBox>
                    </DSBox>
                  ))}
                </DSBox>
              </>
            )}
            {!loading && (
              <form
                onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
                style={{ width: "90%" }}
              >
                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  {(action === DeviceCommand.PREVIEW ||
                    action === DeviceCommand.DELETE) && (
                    <DSBox
                      sx={{
                        width: "90%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        mt: "10px",
                        mb: "10px",
                      }}
                    >
                      <DSSecondaryButton
                        type="button"
                        onClick={() => {
                          setIsKlydoModalOpen(true);
                        }}
                      >
                        Select Klydos
                      </DSSecondaryButton>
                      {isKlydoModalOpen && (
                        <KlydosPickDialog
                          selectedKlydos={params}
                          onPickKlydos={handlePickKlydo}
                          onClose={() => setIsKlydoModalOpen(false)}
                          limit={
                            action === DeviceCommand.DELETE
                              ? 1
                              : deviceVersion && deviceVersion >= 590
                                ? 5
                                : 1
                          }
                        />
                      )}
                    </DSBox>
                  )}

                  {action === DeviceCommand.LOGCAT && (
                    <DSBox>
                      <DSTypography>
                        Are you sure about the action?
                      </DSTypography>
                    </DSBox>
                  )}
                  {action === DeviceCommand.SYNC_ALL_SETTINGS_TO_FIRESTORE && (
                    <DSBox>
                      <DSTypography>
                        Are you sure about the action?
                      </DSTypography>
                    </DSBox>
                  )}
                  {action === DeviceCommand.SYNC_LOGS && (
                    <DSFormControlLabel
                      sx={{ width: "fit-content", mt: "7px" }}
                      control={
                        <DSCheckBox
                          checked={includeDebugLogs}
                          onChange={() =>
                            setIncludeDebugLogs(!includeDebugLogs)
                          }
                        />
                      }
                      label="Include debug logs"
                    />
                  )}
                  {action === DeviceCommand.PLAY_SOUND && (
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        mt: "7px",
                      }}
                    >
                      <DSFormControlLabel
                        sx={{ width: "fit-content", mb: 0 }}
                        control={
                          <DSCheckBox
                            checked={normalizeSound}
                            onChange={() => setNormalizeSound(!normalizeSound)}
                          />
                        }
                        label="Normalize sound (same processing as chime uploads)"
                      />
                      <label style={{ fontSize: "12px", marginBottom: 0 }}>
                        Upload a sound file (fills the URL below):
                        <input
                          style={{ display: "block", marginTop: "5px" }}
                          type="file"
                          accept=".mp3, .wav, .m4a, .ogg"
                          disabled={isUploadingSound}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setSoundUploadError("");
                            try {
                              const reader = new FileReader();
                              reader.onload = async () => {
                                try {
                                  const base64 = reader.result as string;
                                  const response = await uploadSound({
                                    file: base64,
                                    fileName: file.name,
                                    fileType: file.type,
                                    normalize: normalizeSound,
                                  });
                                  if (
                                    response.status === "approved" &&
                                    response.url
                                  ) {
                                    setSoundUrl(response.url);
                                  } else {
                                    setSoundUploadError(
                                      response.error || "Upload failed",
                                    );
                                  }
                                } catch (error) {
                                  setSoundUploadError(
                                    "Failed to upload sound" +
                                      (error && (error as Error).message
                                        ? `: ${(error as Error).message}`
                                        : ""),
                                  );
                                }
                              };
                              reader.readAsDataURL(file);
                            } catch (error) {
                              setSoundUploadError(
                                "Failed to upload sound" +
                                  (error && (error as Error).message
                                    ? `: ${(error as Error).message}`
                                    : ""),
                              );
                            }
                          }}
                        />
                      </label>
                      {isUploadingSound && (
                        <DSBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            fontSize: "12px",
                          }}
                        >
                          <DSCircularProgress size={20} />
                          Uploading...
                        </DSBox>
                      )}
                      {soundUploadError && (
                        <DSBox sx={{ color: "error.main", fontSize: "12px" }}>
                          {soundUploadError}
                        </DSBox>
                      )}
                      <DSTextField
                        type="text"
                        placeholder="Sound file URL (e.g. https://.../sound.mp3)"
                        value={soundUrl}
                        onChange={(e) => setSoundUrl(e.target.value)}
                      />
                      {!!soundUrl.trim() && (
                        <AudioPlayer src={soundUrl.trim()} />
                      )}
                      <DSTextField
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="Volume 0-100 (optional, default 100)"
                        value={soundVolume}
                        onChange={(e) => setSoundVolume(e.target.value)}
                      />
                    </DSBox>
                  )}
                  {action === DeviceCommand.RUN_MIGRATIONS && (
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        mt: "7px",
                      }}
                    >
                      <DSFormControlLabel
                        sx={{ width: "fit-content" }}
                        control={
                          <DSCheckBox
                            checked={rootMigration}
                            onChange={() => setRootMigration(!rootMigration)}
                          />
                        }
                        label="ROOT_MIGRATION"
                      />
                      <DSFormControlLabel
                        sx={{ width: "fit-content" }}
                        control={
                          <DSCheckBox
                            checked={roomMigration}
                            onChange={() => setRoomMigration(!roomMigration)}
                          />
                        }
                        label="ROOM_MIGRATION"
                      />
                      <DSBox>
                        <DSFormControlLabel
                          sx={{ width: "fit-content" }}
                          control={
                            <DSCheckBox
                              checked={rebuildRoomDb}
                              onChange={() => setRebuildRoomDb(!rebuildRoomDb)}
                            />
                          }
                          label="REBUILD_ROOM_DB"
                        />
                        <DSBox
                          sx={{
                            color: "error.main",
                            fontSize: "12px",
                            mt: "5px",
                            ml: "20px",
                            fontWeight: "bold",
                          }}
                        >
                          ⚠️ DESTRUCTIVE: Deletes all data. Sync afterwards!
                        </DSBox>
                      </DSBox>
                    </DSBox>
                  )}
                  {action === DeviceCommand.RUN_MONITORS && (
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        mt: "7px",
                      }}
                    >
                      <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={clockMonitorNameOptions}
                        value={monitorNames}
                        onChange={(_, newValue) => setMonitorNames(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              monitorNames.length === 0
                                ? "Pick monitors or leave empty for all"
                                : ""
                            }
                          />
                        )}
                      />
                      <DSTypography
                        sx={{ fontSize: "12px", color: "text.secondary" }}
                      >
                        Pick clock monitors to run, or type a name and press
                        Enter. Leave empty to run all monitors on the device.
                        {monitorNames.length > 0 &&
                          ` Will run ${monitorNames.length} monitor${
                            monitorNames.length > 1 ? "s" : ""
                          }.`}
                      </DSTypography>
                    </DSBox>
                  )}
                </DSBox>
              </form>
            )}
          </DSDialogContent>

          <DSDialogActions
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <DSBox sx={{ width: "fit-content" }}>
              <DSSecondaryButton
                onClick={() => handleClose(false)}
                size="small"
                sx={{ ...actionButtonSx, width: "100%" }}
              >
                Cancel
              </DSSecondaryButton>
            </DSBox>
            {(action === DeviceCommand.UPDATE ||
              action === DeviceCommand.UPDATE_REMOTE ||
              action === DeviceCommand.ONBOARDING) && <ActionButton />}
            {(action === DeviceCommand.DELETE ||
              action === DeviceCommand.PREVIEW) && (
              <DSPrimaryButton
                disabled={params.length === 0}
                onClick={() => {
                  setShowCmdSummary(true);
                }}
                size="small"
                sx={actionButtonSx}
              >
                {"send"}
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SYNC && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Sync
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SYNC_LOGS && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Sync Logs
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.UPDATE_LOG_CONFIG && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Update Log Config
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.BLUETOOTH && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Reset Bluetooth
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.GET_GIFT && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Get Gift
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.LOGCAT && (
              <DSPrimaryButton
                onClick={(event) => {
                  event.preventDefault();
                  handleLogcat();
                }}
                size="small"
                sx={actionButtonSx}
              >
                Open Logcat
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SEND_ROOM_DB && (
              <DSPrimaryButton
                onClick={(event) => {
                  event.preventDefault();
                  handleRoomDb();
                }}
                size="small"
                sx={actionButtonSx}
              >
                Get Room DB
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.STREAM_SQL_QUERIES && (
              <DSPrimaryButton
                onClick={(event) => {
                  event.preventDefault();
                  handleSqlStream();
                }}
                size="small"
                sx={actionButtonSx}
              >
                Stream SQL Queries
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SYNC_ALL_SETTINGS_TO_FIRESTORE && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Sync All Settings
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.RUN_MIGRATIONS && (
              <DSPrimaryButton
                disabled={!rootMigration && !roomMigration && !rebuildRoomDb}
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Run Migrations
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.PLAY_SOUND && (
              <DSPrimaryButton
                disabled={!soundUrl.trim() || isUploadingSound}
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Play Sound
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.STREAM_ADB_SHELL && (
              <DSPrimaryButton
                onClick={(event) => {
                  event.preventDefault();
                  handleAdbShell();
                }}
                size="small"
                sx={actionButtonSx}
              >
                Open ADB Shell
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SYNC_TIME && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Sync Time
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SEND_CRASH_BUNDLE && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Get Crash Bundle
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.RUN_MONITORS && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Run Monitors
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SYNC_MONITOR_REPORTS && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Sync Monitor Reports
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.FETCH_RATING_FEED && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Fetch Rating Feed
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.RESET_RATINGS && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Reset Ratings
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.TRIGGER_ECO_MODE && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Trigger ECO Mode
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.REFRESH_CONFIG && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Refresh Config
              </DSPrimaryButton>
            )}
            {action === DeviceCommand.SYNC_ANALYTICS && (
              <DSPrimaryButton
                onClick={() => setShowCmdSummary(true)}
                size="small"
                sx={actionButtonSx}
              >
                Sync Analytics
              </DSPrimaryButton>
            )}
            {errors.serverError && (
              <DSTypography color="error">{errors.serverError}</DSTypography>
            )}
          </DSDialogActions>
        </DSDialog>
      )}
      {isLogcatOpen && (
        <LogcatStreamModal
          deviceId={selectedDevices[0]}
          onClose={() => {
            setIsLogcatOpen(false);
            handleClose(false);
          }}
        />
      )}
      {isRoomDbOpen && (
        <RoomDbViewerModal
          deviceId={selectedDevices[0]}
          onClose={() => {
            setIsRoomDbOpen(false);
            handleClose(false);
          }}
        />
      )}
      {isSqlStreamOpen && (
        <SqlQueryStreamModal
          deviceId={selectedDevices[0]}
          onClose={() => {
            setIsSqlStreamOpen(false);
            handleClose(false);
          }}
        />
      )}
      {isAdbShellOpen && (
        <AdbShellStreamModal
          deviceId={selectedDevices[0]}
          onClose={() => {
            setIsAdbShellOpen(false);
            handleClose(false);
          }}
        />
      )}
      {showCmdSummary && (
        <DSDialog
          open={showCmdSummary}
          onClose={() => setShowCmdSummary(false)}
          fullWidth
          maxWidth="sm"
        >
          <DSDialogContent>
            <DSBox sx={{ m: "25px" }}>
              <DSTypography variant="h6" sx={{ textAlign: "center" }}>
                You are about to {getHeadline(action)} on{" "}
                {selectedDevices.length} devices.
              </DSTypography>
            </DSBox>
            <DSAlert
              severity="error"
              icon={false}
              sx={{ justifyContent: "center", textAlign: "center" }}
            >
              <DSTypography sx={{ m: "2px", fontWeight: "bold" }}>
                This action cannot be undone
              </DSTypography>
            </DSAlert>
            {allowsExpiry && (
              <DSBox sx={{ mt: 2 }}>
                <DSTextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Expires at (optional)"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: toDateTimeLocalValue(new Date()),
                    max: toDateTimeLocalValue(maxCommandExpiresAt()),
                  }}
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  error={!!expiresAtError}
                  helperText={
                    expiresAtError ||
                    `Leave empty for the default (~1 day). Up to ${MAX_COMMAND_EXPIRY_DAYS} days. Offline clocks run the command when they reconnect, until it expires.`
                  }
                />
              </DSBox>
            )}
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mt: 2,
              }}
            >
              <DSSecondaryButton
                sx={{ width: "45%" }}
                onClick={() => setShowCmdSummary(false)}
              >
                Cancel
              </DSSecondaryButton>
              <DSErrorButton
                title="Submit cannot be undone"
                variant="contained"
                sx={{ width: "45%" }}
                disabled={!!expiresAtError}
                onClick={(event) => {
                  event.preventDefault();
                  handleSubmit();
                }}
              >
                {loading && <DSCircularProgress size={20} sx={{ mr: 1 }} />}
                Submit
              </DSErrorButton>
            </DSBox>
          </DSDialogContent>
        </DSDialog>
      )}
    </>
  );
}

export default ScheduleBulkTaskModal;
